import { useState } from "react";

import { Button, Icon, Pill } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useMfaDisable } from "@/hooks/useMfa";
import { MfaSetupFlow } from "./MfaSetupFlow";
import "./Mfa.css";

type Mode = "idle" | "setup" | "disable";

/** MFA (TOTP) enable/disable controls — embeddable (no PageHead/Modal of its own). */
export function MfaManager() {
  const { user, refresh } = useAuth();
  const disable = useMfaDisable();

  const [mode, setMode] = useState<Mode>("idle");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const enabled = user?.mfaEnabled ?? false;

  async function onActivated() {
    await refresh();
    setMode("idle");
  }

  async function onDisable() {
    setError(null);
    try {
      await disable.mutateAsync(code.trim());
      await refresh();
      setMode("idle");
      setCode("");
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <div className="verify-row">
      <div className="verify-row-icon">
        <Icon name="shield-check" size={18} />
      </div>
      <div className="verify-row-body">
        <div className="verify-row-title">
          <span>Autenticação em dois fatores</span>
          {enabled ? <Pill tone="success">Ativado</Pill> : <Pill tone="muted">Desativado</Pill>}
        </div>
        <p className="verify-row-text">
          Use um aplicativo autenticador (TOTP) para exigir um segundo fator no login.
          {user?.isPlatformAdmin && " Obrigatório para administradores da plataforma."}
        </p>

        {mode === "setup" && (
          <MfaSetupFlow onActivated={onActivated} onCancel={() => setMode("idle")} />
        )}

        {mode === "disable" && (
          <div className="mfa-setup">
            <p className="mfa-step-text">
              Digite um código atual do seu aplicativo autenticador para confirmar.
            </p>
            {error && <div className="login-error">{error}</div>}
            <input
              className="mfa-otp-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="Código de 6 dígitos"
              autoComplete="one-time-code"
              autoFocus
            />
            <div className="mfa-actions">
              <Button variant="ghost" onClick={() => setMode("idle")} disabled={disable.isPending}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={onDisable}
                disabled={disable.isPending || code.length !== 6}
              >
                {disable.isPending ? "Desativando…" : "Desativar"}
              </Button>
            </div>
          </div>
        )}

        {mode === "idle" &&
          (enabled ? (
            <Button variant="outline" size="sm" onClick={() => setMode("disable")}>
              Desativar
            </Button>
          ) : (
            <Button variant="primary" size="sm" onClick={() => setMode("setup")}>
              Ativar
            </Button>
          ))}
      </div>
    </div>
  );
}
